import { useCallback } from 'react';
import type { Fetcher } from 'swr';
import useSWR, { useSWRConfig } from 'swr';
import type { MutationFetcher } from 'swr/mutation';
import useSWRMutation from 'swr/mutation';

import { ticketsDestroy, ticketsRetrieve } from '~/data/generated/endpoints/tickets/tickets';
import { useRefetchedOnOpen } from '~/data/hooks/useRefetchedOnOpen';
import { getTicketMutator } from '~/data/hooks/utils';
import { parseApiResponse } from '~/data/parseApiResponse';
import { type API_TicketDetailResponse, ticketSchema } from '~/data/schemas/ticket';

const ticketFetcher: Fetcher<
  API_TicketDetailResponse,
  readonly ['ticket', ticketId: string]
> = async ([_, ticketId]) => {
  const { data } = await ticketsRetrieve(ticketId);
  return parseApiResponse(ticketSchema, data, 'Ticket');
};

const ticketDeleteFetcher: MutationFetcher<void, readonly [key: string], { id: string }> = async (
  [key],
  { arg: { id } },
) => {
  await ticketsDestroy(id);
};

const useTicketDelete = () => {
  const { mutate } = useSWRConfig();

  const { trigger } = useSWRMutation(['ticket'], ticketDeleteFetcher);

  const deleteTicker = useCallback(
    async (ticketId: string) => {
      if (!ticketId) {
        throw new Error('Invoice ID is required to update an invoice');
      }

      await trigger({ id: ticketId }, { revalidate: false });

      void mutate(getTicketMutator(ticketId));
    },
    [mutate, trigger],
  );

  return deleteTicker;
};

export default function useTicket(ticketId: string | null) {
  const deleteTicket = useTicketDelete();

  const {
    data,
    error,
    isLoading: loading,
  } = useSWR(ticketId ? (['ticket', ticketId] as const) : null, ticketFetcher);

  return { ticket: data, error, loading, deleteTicket };
}

export function useTicketPreview(ticketId: string | null) {
  // Cached document links may have expired, so show the ticket only once refetched.
  const { data, error, loading } = useRefetchedOnOpen(
    ticketId ? (['ticket', ticketId] as const) : null,
    ticketFetcher,
  );

  return { ticket: data, error, loading };
}
